import { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, Shield, ArrowLeft } from "lucide-react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import phoneMp4 from "../assets/phone.mp4";
import phoneWebm from "../assets/phone.webm";

const Landing = () => {
  const navigate = useNavigate();
  const { login, verify2FA, cancel2FA } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // 2FA state
  const [show2FA, setShow2FA] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [verifying, setVerifying] = useState(false);

  // Mouse-reactive 3D tilt for the hero scene
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const rafRef = useRef(null);

  const handleMouseMove = (e) => {
    const px = e.clientX / window.innerWidth - 0.5; // -0.5 .. 0.5
    const py = e.clientY / window.innerHeight - 0.5;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => setTilt({ x: px, y: py }));
  };
  const handleMouseLeave = () => setTilt({ x: 0, y: 0 });

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return;

    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.requires_2fa) {
        setShow2FA(true);
        setLoading(false);
        return;
      }
      toast.success("Успешный вход!");
      navigate(result.user?.is_admin ? "/admin" : "/dashboard");
    } catch (error) {
      toast.error(error.message || "Ошибка входа");
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerify = async () => {
    if (otpCode.length !== 6) return;
    setVerifying(true);
    try {
      const data = await verify2FA(otpCode);
      toast.success("Успешный вход!");
      navigate(data.user?.is_admin ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Неверный код");
      setOtpCode("");
    } finally {
      setVerifying(false);
    }
  };

  const handleBack = () => {
    cancel2FA();
    setShow2FA(false);
    setOtpCode("");
  };

  const filled = email && password;

  return (
    <div
      className="relative min-h-screen w-full overflow-x-hidden flex flex-col lg:flex-row select-none"
      style={{ background: "#000" }}
      data-testid="landing-page"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* ===================== LEFT COLUMN — AUTH (≈40%) ===================== */}
      <div className="relative z-20 w-full lg:w-[42%] flex flex-col px-6 sm:px-10 lg:px-14 py-8 lg:py-10 min-h-screen">
        {/* Ambient glow removed for a flat premium look */}

        {/* Logo top */}
        <div className="relative animate-slide-left" style={{ animationDelay: "0.1s" }} data-testid="w3-logo">
          <span className="w3-shimmer font-chivo font-bold tracking-tight" style={{ fontSize: "2rem" }}>
            Web-3
          </span>
        </div>

        {/* Center — mobile gif + auth card */}
        <div className="relative flex-1 flex flex-col items-center justify-center gap-5 lg:gap-0">
          {/* Mobile-only looping video (desktop shows it in the right column) */}
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="lg:hidden object-contain"
            style={{
              width: "auto",
              maxWidth: "240px",
              maxHeight: "26vh",
              WebkitMaskImage:
                "radial-gradient(ellipse 54% 60% at 50% 50%, #000 26%, rgba(0,0,0,0.5) 54%, transparent 78%)",
              maskImage:
                "radial-gradient(ellipse 54% 60% at 50% 50%, #000 26%, rgba(0,0,0,0.5) 54%, transparent 78%)",
            }}
            data-testid="hero-video-mobile"
          >
            <source src={phoneWebm} type="video/webm" />
            <source src={phoneMp4} type="video/mp4" />
          </video>

          <div
            className="w-full max-w-md animate-slide-left"
            style={{
              animationDelay: "0.3s",
              background: "#0A0A0A",
              border: "1px solid #1F1F1F",
              boxShadow: "none",
              borderRadius: "16px",
              padding: "44px 38px 38px",
            }}
            data-testid="login-card"
          >
            {show2FA ? (
              /* ---- 2FA VERIFICATION ---- */
              <>
                <div className="flex justify-center mb-6">
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center"
                    style={{ background: "rgba(255,255,255,0.06)" }}
                  >
                    <Shield size={28} style={{ color: "#EDEDED" }} />
                  </div>
                </div>
                <h1
                  className="font-chivo font-semibold text-white text-center tracking-wide"
                  style={{ fontSize: "1.4rem", marginBottom: "10px" }}
                >
                  Двухфакторная аутентификация
                </h1>
                <p className="text-center text-white/35 text-sm mb-8">
                  Введите 6-значный код из приложения
                </p>
                <div className="flex justify-center mb-6">
                  <InputOTP maxLength={6} value={otpCode} onChange={setOtpCode} data-testid="otp-input">
                    <InputOTPGroup>
                      {[0, 1, 2, 3, 4, 5].map((i) => (
                        <InputOTPSlot key={i} index={i} className="!bg-[#0A0A0E] !border-white/10 !text-white" />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <button
                  onClick={handle2FAVerify}
                  disabled={verifying || otpCode.length !== 6}
                  className="w-full py-3.5 rounded-xl font-semibold transition-all duration-300 disabled:cursor-not-allowed"
                  style={{
                    background:
                      otpCode.length === 6
                        ? "linear-gradient(135deg, #FF8A1E, #E67A00)"
                        : "rgba(255,255,255,0.04)",
                    boxShadow: otpCode.length === 6 ? "none" : "none",
                    color: otpCode.length === 6 ? "#fff" : "rgba(255,255,255,0.18)",
                  }}
                  data-testid="verify-2fa-btn"
                >
                  {verifying ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Подтвердить"}
                </button>
                <button
                  onClick={handleBack}
                  className="w-full mt-4 text-center text-sm text-white/30 hover:text-white/50 transition-colors flex items-center justify-center gap-1.5"
                  data-testid="back-to-login-btn"
                >
                  <ArrowLeft size={14} /> Назад
                </button>
              </>
            ) : (
              /* ---- LOGIN FORM ---- */
              <>
                <h1
                  className="font-chivo font-semibold text-white text-center tracking-wide"
                  style={{ fontSize: "1.55rem", marginBottom: "32px", letterSpacing: "0.04em" }}
                >
                  Авторизация
                </h1>

                <form onSubmit={handleLogin} className="space-y-5">
                  {/* Email */}
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                    className="landing-input"
                    data-testid="landing-email-input"
                  />

                  {/* Password */}
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Пароль"
                      className="landing-input pr-12"
                      data-testid="landing-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-white/20 hover:text-white/50 transition-colors"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={loading || !filled}
                    className="w-full py-3.5 rounded-xl font-semibold transition-all duration-300 disabled:cursor-not-allowed"
                    style={{
                      background: filled
                        ? "linear-gradient(135deg, #FF8A1E, #E67A00)"
                        : "rgba(255,255,255,0.04)",
                      boxShadow: filled ? "none" : "none",
                      color: filled ? "#fff" : "rgba(255,255,255,0.18)",
                    }}
                    data-testid="landing-submit-btn"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Войти"}
                  </button>
                </form>

                <p className="text-center text-sm mt-7" style={{ color: "rgba(255,255,255,0.3)" }}>
                  Нет аккаунта?{" "}
                  <Link
                    to="/register"
                    className="hover:opacity-75 transition-opacity"
                    style={{ color: "#EDEDED" }}
                    data-testid="landing-register-link"
                  >
                    Зарегистрируйтесь
                  </Link>
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ===================== RIGHT COLUMN — HERO VIDEO (≈60%) ===================== */}
      <div
        className="relative hidden lg:flex w-[58%] items-center justify-center overflow-visible animate-slide-right"
        style={{ animationDelay: "0.2s", background: "#000", perspective: "1500px" }}
        data-testid="landing-hero"
      >
        {/* Floating wrapper (continuous breathing motion) + looping phone video */}
        <div
          className="hero-float relative z-10"
          style={{ transformStyle: "preserve-3d", willChange: "transform" }}
        >
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="object-contain"
            style={{
              width: "auto",
              maxWidth: "500px",
              maxHeight: "74vh",
              transform: `rotateY(${tilt.x * 10}deg) rotateX(${tilt.y * -8}deg) translateX(${tilt.x * 18}px)`,
              transformStyle: "preserve-3d",
              transition: "transform 0.25s cubic-bezier(0.22,1,0.36,1)",
              WebkitMaskImage:
                "radial-gradient(ellipse 54% 60% at 50% 50%, #000 26%, rgba(0,0,0,0.5) 54%, transparent 78%)",
              maskImage:
                "radial-gradient(ellipse 54% 60% at 50% 50%, #000 26%, rgba(0,0,0,0.5) 54%, transparent 78%)",
            }}
            data-testid="hero-video"
          >
            <source src={phoneWebm} type="video/webm" />
            <source src={phoneMp4} type="video/mp4" />
          </video>
        </div>

        {/* Soft seam blend toward the left auth column (no hard crop) */}
        <div
          className="absolute inset-y-0 left-0 pointer-events-none"
          style={{
            width: "26%",
            background: "linear-gradient(90deg, #000 0%, rgba(0,0,0,0.5) 45%, transparent 100%)",
          }}
        />
      </div>
    </div>
  );
};

export default Landing;
